import React, { useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import ForceGraph2D from "react-force-graph-2d";
import { Box, Typography, Button, Divider, Grid, Paper } from "@mui/material";

const binToName = {
  1: "ACP",
  2: "ARM",
  4: "DON",
  8: "HPB",
  16: "NEG",
  32: "POS",
  128: "SSB",
  256: "SALT_BRIDGE",
  512: "HYDROPHOBIC",
  1024: "H_BOND",
  2048: "ARM_STACK"
};

const decodeBitmask = (bitmask = 0) =>
  Object.entries(binToName)
    .filter(([bit]) => bitmask & Number(bit))
    .map(([, name]) => name);


const inferNodeType = (node) => {
  const { residueName, atomName, isLigand } = node;

  let type = 0;

  // Aromatic residues
  if (["PHE", "TYR", "TRP", "HIS"].includes(residueName)) {
    type |= 2; // ARM
  }

  // Hydrogen bond donors / acceptors
  if (["OH", "NH", "NE", "ND"].some(x => atomName.startsWith(x))) {
    type |= 1024; // H_BOND
  }

  // Charged residues
  if (["LYS", "ARG"].includes(residueName)) {
    type |= 32; // POS
  }

  if (["ASP", "GLU"].includes(residueName)) {
    type |= 16; // NEG
  }

  // Hydrophobic carbons (very common)
  if (atomName.startsWith("C")) {
    type |= 512; // HYDROPHOBIC
  }

  return type;
};

const inferEdgeType = (link) => {
  const { distance } = link;

  let type = 0;

  if (distance <= 3.2) {
    type |= 1024; // H_BOND
  }

  if (distance > 3.2 && distance <= 4.0) {
    type |= 512; // HYDROPHOBIC
  }

  // Optional: salt bridge at close charged contacts
  if (distance <= 3.5) {
    type |= 256; // SALT_BRIDGE
  }

  return type;
};




export default function GraphViews() {
  const { state } = useLocation();
  const navigate = useNavigate();
  const row = state?.rowData;

  function convertGraphToTypedGraph(rawGraph) {
    return {
        nodes: rawGraph.nodes.map(n => ({
        ...n,
        type: inferNodeType(n)
        })),

        links: rawGraph.links.map(l => ({
        ...l,
        type: inferEdgeType(l)
        }))
    };
  }


  // ---------------- DEMO DATA (fallback) ----------------
  const demoTargetGraphOld = {
  nodes: [
    { id: 0, chain: "A", residueName: "LYS", residueNumber: 353, atomName: "CB", isLigand: 0, type: 32 },   // POS
    { id: 1, chain: "A", residueName: "TYR", residueNumber: 41, atomName: "CE2", isLigand: 0, type: 2 },    // ARM
    { id: 2, chain: "B", residueName: "TYR", residueNumber: 501, atomName: "CD1", isLigand: 1, type: 2 },
    { id: 3, chain: "B", residueName: "TYR", residueNumber: 501, atomName: "CE1", isLigand: 1, type: 1024 }, // H_BOND donor
    { id: 4, chain: "A", residueName: "TYR", residueNumber: 41, atomName: "CZ", isLigand: 0, type: 512 },
    { id: 5, chain: "A", residueName: "TYR", residueNumber: 41, atomName: "CD1", isLigand: 0, type: 2 },
    { id: 6, chain: "A", residueName: "TYR", residueNumber: 41, atomName: "CE1", isLigand: 0, type: 2 }
  ],
  links: [
    { source: 0, target: 2, distance: 3.756, type: 1024 }, // H_BOND
    { source: 0, target: 3, distance: 3.783, type: 256 },  // SALT_BRIDGE
    { source: 1, target: 2, distance: 3.750, type: 512 },  // HYDROPHOBIC
    { source: 1, target: 3, distance: 3.623, type: 1024 },
    { source: 2, target: 4, distance: 3.485, type: 512 },
    { source: 3, target: 5, distance: 3.719, type: 2048 },
    { source: 3, target: 6, distance: 3.415, type: 1024 },
    { source: 3, target: 4, distance: 3.361, type: 512 }
  ]
};

const targetGraphRaw = {
  nodes: [
    { id: 0, label: "0", chain: "A", residueName: "LYS", residueNumber: 353, atomName: "CB", isLigand: 0 },
    { id: 1, label: "1", chain: "A", residueName: "TYR", residueNumber: 41, atomName: "CE2", isLigand: 0 },
    { id: 2, label: "2", chain: "B", residueName: "TYR", residueNumber: 501, atomName: "CD1", isLigand: 1 },
    { id: 3, label: "3", chain: "B", residueName: "TYR", residueNumber: 501, atomName: "CE1", isLigand: 1 },
    { id: 4, label: "4", chain: "A", residueName: "TYR", residueNumber: 41, atomName: "CZ", isLigand: 0 },
    { id: 5, label: "5", chain: "A", residueName: "TYR", residueNumber: 41, atomName: "CD1", isLigand: 0 },
    { id: 6, label: "6", chain: "A", residueName: "TYR", residueNumber: 41, atomName: "CE1", isLigand: 0 }
  ],
  links: [
    { source: 0, target: 2, distance: 3.756 },
    { source: 0, target: 3, distance: 3.783 },
    { source: 1, target: 2, distance: 3.750 },
    { source: 1, target: 3, distance: 3.623 },
    { source: 2, target: 4, distance: 3.485 },
    { source: 3, target: 5, distance: 3.719 },
    { source: 3, target: 6, distance: 3.415 },
    { source: 3, target: 4, distance: 3.361 }
  ]
};

const ligandGraphRaw = {
  nodes: [
    { id: 0, chain: "A", residueName: "TYR", residueNumber: 114, atomName: "CD2", isLigand: 0 },
    { id: 1, chain: "A", residueName: "PHE", residueNumber: 87, atomName: "CG", isLigand: 0 },
    { id: 2, chain: "B", residueName: "ALA", residueNumber: 120, atomName: "CB", isLigand: 1 },
    { id: 3, chain: "B", residueName: "TYR", residueNumber: 105, atomName: "CD2", isLigand: 1 },
    { id: 4, chain: "A", residueName: "PHE", residueNumber: 87, atomName: "CE2", isLigand: 0 },
    { id: 5, chain: "A", residueName: "PHE", residueNumber: 87, atomName: "CZ", isLigand: 0 },
    { id: 6, chain: "B", residueName: "PHE", residueNumber: 95, atomName: "CB", isLigand: 1 },
    { id: 7, chain: "A", residueName: "PHE", residueNumber: 87, atomName: "CD2", isLigand: 0 },
    { id: 8, chain: "B", residueName: "PHE", residueNumber: 95, atomName: "CG", isLigand: 1 },
    { id: 9, chain: "B", residueName: "PHE", residueNumber: 95, atomName: "CD2", isLigand: 1 },
    { id: 10, chain: "B", residueName: "TYR", residueNumber: 105, atomName: "CE2", isLigand: 1 },
    { id: 11, chain: "B", residueName: "ILE", residueNumber: 107, atomName: "CD1", isLigand: 1 },
    { id: 12, chain: "A", residueName: "PHE", residueNumber: 87, atomName: "CE1", isLigand: 0 }
  ],
  links: [
    { source: 0, target: 2, distance: 3.65 },
    { source: 1, target: 2, distance: 3.53 },
    { source: 2, target: 7, distance: 3.23 },
    { source: 2, target: 4, distance: 3.41 },
    { source: 3, target: 12, distance: 3.43 },
    { source: 3, target: 5, distance: 3.78 },
    { source: 4, target: 11, distance: 3.39 },
    { source: 5, target: 11, distance: 3.42 },
    { source: 5, target: 8, distance: 3.48 },
    { source: 5, target: 6, distance: 3.60 },
    { source: 5, target: 9, distance: 3.46 },
    { source: 6, target: 12, distance: 3.67 },
    { source: 8, target: 12, distance: 3.79 },
    { source: 9, target: 12, distance: 3.49 },
    { source: 10, target: 12, distance: 3.41 }
  ]
};

const demoTargetGraph = convertGraphToTypedGraph(targetGraphRaw);

const demoLigandGraph = convertGraphToTypedGraph(ligandGraphRaw);

  

  const demoMappings = [{ target: 1, ligand: "a" }];

  // ---------------- DATA SELECTION ----------------
  const targetGraph = row?.targetGraph || demoTargetGraph;
  const ligandGraph = row?.ligandGraph || demoLigandGraph;
  const mappings = row?.mappings || demoMappings;

  // ---------------- SECTION 1: TARGET GRAPH ----------------
 const targetGraphData = useMemo(() => ({
    nodes: targetGraph.nodes.map(n => {
        const atomicTypes = decodeBitmask(n.type); // NODE types

        return {
        ...n,
        id: "T-" + n.id,
        group: "target",
        atomicTypes,
        label: `${n.residueName}${n.residueNumber}:${n.atomName}
    ${atomicTypes.join("/")}
    Chain ${n.chain}`
        };
    }),

    links: targetGraph.links.map(l => {
        const interactionTypes = decodeBitmask(l.type); // EDGE types

        return {
        source: "T-" + l.source,
        target: "T-" + l.target,
        interactionTypes,
        label: interactionTypes.join("/") || ""
        };
    })
 }), [targetGraph]);


  // ---------------- SECTION 2: LIGAND GRAPH ----------------
  const ligandGraphData = useMemo(() => ({
    nodes: ligandGraph.nodes.map(n => {
        const atomicTypes = decodeBitmask(n.type);

        return {
        ...n,
        id: "L-" + n.id,
        group: "ligand",
        atomicTypes,
        label: `${n.residueName}${n.residueNumber}:${n.atomName}
    ${atomicTypes.join("/")}
    Chain ${n.chain}`
        };
    }),

    links: ligandGraph.links.map(l => {
        const interactionTypes = decodeBitmask(l.type);

        return {
        source: "L-" + l.source,
        target: "L-" + l.target,
        interactionTypes,
        label: interactionTypes.join("/") || ""
        };
    })
  }), [ligandGraph]);



  // ---------------- SECTION 3: ATOM MAPPING GRAPH ----------------
  const mappingGraphData = useMemo(() => {
    const tNodes = targetGraph.nodes.map(n => ({ ...n, id: "T-" + n.id, group: "target" }));
    const lNodes = ligandGraph.nodes.map(n => ({ ...n, id: "L-" + n.id, group: "ligand" }));

    const tLinks = targetGraph.links.map(l => ({
      source: "T-" + l.source,
      target: "T-" + l.target
    }));

    const lLinks = ligandGraph.links.map(l => ({
      source: "L-" + l.source,
      target: "L-" + l.target
    }));

    const mapLinks = mappings
    .filter(m => 
        targetGraph.nodes.find(n => n.id === m.target) &&
        ligandGraph.nodes.find(n => n.id === m.ligand)
    )
    .map(m => ({
        source: "T-" + m.target,
        target: "L-" + m.ligand,
        color: "red",
        dashed: true
    }));

    return {
      nodes: [...tNodes, ...lNodes],
      links: [...tLinks, ...lLinks, ...mapLinks]
    };
  }, [targetGraph, ligandGraph, mappings]);

  // ---------------- SECTION 4: CHAIN MAPPING GRAPH ----------------
  const chainGraphData = useMemo(() => {
    if (!row?.Chain_Mappings) {
        return {
        nodes: [
            { id: "T-A", label: "Target Chain A", group: "target" },
            { id: "L-B", label: "Ligand Chain B", group: "ligand" }
        ],
        links: [{ source: "T-A", target: "L-B" }]
        };
    }

    const targetNodes = Object.keys(row.Chain_Mappings || {}).map(t => ({
        id: `T-${t}`,
        label: `Target ${t}`,
        group: "target"
    }));

    const ligandChains = new Set();
    Object.values(row.Chain_Mappings || {}).forEach(maps =>
        maps.forEach(m => ligandChains.add(m.lig_chain))
    );

    const ligandNodes = [...ligandChains].map(l => ({
        id: `L-${l}`,
        label: `Ligand ${l}`,
        group: "ligand"
    }));

    const links = [];
    Object.entries(row.Chain_Mappings || {}).forEach(([t, maps]) => {
        maps.forEach(m => {
        links.push({
            source: `T-${t}`,
            target: `L-${m.lig_chain}`
        });
        });
    });

    return { nodes: [...targetNodes, ...ligandNodes], links };
  }, [row]);

  // ---------------- SECTION 5: COMPLEX GRAPH (DEMO) ----------------
  const complexGraphData = {
    nodes: [
      { id: "P1", label: "Protein", group: "target" },
      { id: "L1", label: "Ligand", group: "ligand" }
    ],
    links: [{ source: "P1", target: "L1" }]
  };

  if (!row) return <Typography>No graph data</Typography>;

  return (
    <Box sx={{ p: 3, minHeight: "92vh" }}>
      <Typography variant="h4" align="center" gutterBottom>
        Graph Visualization Dashboard
      </Typography>

      <Grid container spacing={3}>

        {/* 1 Target Graph */}
        <Grid item xs={12}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6">1️⃣ Complete Target Graph</Typography>
            <ForceGraph2D
                graphData={targetGraphData}
                nodeLabel={n => n.label}
                nodeColor={n => n.isLigand ? "#9BCE91" : "#7CB4BE"}

                linkCanvasObjectMode={() => "after"}   // REQUIRED

                linkCanvasObject={(link, ctx) => {
                    if (!link.label) return;

                    const src = link.source;
                    const trg = link.target;
                    if (!src || !trg) return;

                    // Midpoint
                    const x = (src.x + trg.x) / 2;
                    const y = (src.y + trg.y) / 2;

                    // Angle of the edge
                    let angle = Math.atan2(trg.y - src.y, trg.x - src.x);
                    if (angle > Math.PI / 2 || angle < -Math.PI / 2) {
                        angle += Math.PI;
                    }

                    ctx.fillStyle = link.interactionTypes?.includes("H_BOND")
                    ? "#1565c0"
                    : "#000";

                    ctx.save();

                    // Move to midpoint
                    ctx.translate(x, y);

                    


                    // Rotate canvas to align with edge
                    ctx.rotate(angle);

                    // Style
                    ctx.font = "2px Sans-Serif";
                    //ctx.fillText(link.label, 0, -2); // ⬅ closer to edge
                    ctx.fillStyle = "#000";
                    ctx.textAlign = "center";
                    ctx.textBaseline = "middle";

                    // Draw label
                    ctx.fillText(link.label, 0, -1); // slight offset from line

                    ctx.restore();
                }}
                />



          </Paper>
        </Grid>

        {/* 2 Ligand Graph */}
        <Grid item xs={12}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6">2️⃣ Complete Ligand Graph</Typography>
            <ForceGraph2D
                graphData={ligandGraphData}
                nodeLabel={n => n.label}
                nodeColor={n => n.isLigand ? "#9BCE91" : "#7CB4BE"}

                linkCanvasObjectMode={() => "after"}   // REQUIRED

                linkCanvasObject={(link, ctx) => {
                    if (!link.label) return;

                    const src = link.source;
                    const trg = link.target;
                    if (!src || !trg) return;

                    // Midpoint
                    const x = (src.x + trg.x) / 2;
                    const y = (src.y + trg.y) / 2;

                    // Angle of the edge
                    let angle = Math.atan2(trg.y - src.y, trg.x - src.x);
                    if (angle > Math.PI / 2 || angle < -Math.PI / 2) {
                        angle += Math.PI;
                    }

                    ctx.fillStyle = link.interactionTypes?.includes("H_BOND")
                    ? "#1565c0"
                    : "#000";

                    ctx.save();

                    // Move to midpoint
                    ctx.translate(x, y);

                    


                    // Rotate canvas to align with edge
                    ctx.rotate(angle);

                    // Style
                    ctx.font = "2px Sans-Serif";
                    //ctx.fillText(link.label, 0, -2); // ⬅ closer to edge
                    ctx.fillStyle = "#000";
                    ctx.textAlign = "center";
                    ctx.textBaseline = "middle";

                    // Draw label
                    ctx.fillText(link.label, 0, -1); // slight offset from line

                    ctx.restore();
                }}
                />

          </Paper>
        </Grid>

        {/* 3 Mapping Graph */}
        <Grid item xs={12}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6">3️⃣ Atom Mapping (Target ↔ Ligand)</Typography>
            <ForceGraph2D
              graphData={mappingGraphData}
              nodeLabel={n => n.element || n.id}
              nodeColor={n => n.group === "target" ? "#1976d2" : "#2e7d32"}
              linkColor={l => l.color || "#999"}
              linkLineDash={l => l.dashed ? [4, 4] : null}
            />
          </Paper>
        </Grid>

        {/* 4 Chain Mapping */}
        <Grid item xs={12}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6">4️⃣ Chain Mapping Graph</Typography>
            <ForceGraph2D
              graphData={chainGraphData}
              nodeLabel="label"
              nodeColor={n => n.group === "target" ? "#1976d2" : "#2e7d32"}
            />
          </Paper>
        </Grid>

        {/* 5 Ligand Protein Complex */}
        <Grid item xs={12}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6">5️⃣ Ligand–Protein Complex (Demo)</Typography>
            <ForceGraph2D
              graphData={complexGraphData}
              nodeLabel="label"
              nodeColor={n => n.group === "target" ? "#1976d2" : "#2e7d32"}
            />
            <Typography sx={{ mt: 2 }}>
              Complex Details (Demo):<br/>
              Binding Energy: -7.5 kcal/mol<br/>
              Interaction Type: Hydrogen Bond<br/>
              Ligand: {row.PDBID_Lig}<br/>
              Target: {row.PDBID_Targ}
            </Typography>
          </Paper>
        </Grid>

      </Grid>

      <Divider sx={{ my: 3 }} />

      <Button variant="outlined" onClick={() => navigate(-1)}>
        Back
      </Button>
    </Box>
  );
}
